"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  AnswerMatrix,
  DeterminantResult,
  EntryWorking,
  entryLines,
  InverseResult,
  type MatrixCtx,
  RrefResult,
  TraceResult,
} from "@/components/tools/linear-algebra/matrix-results";
import { MatrixGrid } from "@/components/tools/linear-algebra/matrix-view";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  addMatrix,
  type Matrix,
  matrixPower,
  multiplyMatrix,
  parseMatrix,
  scaleMatrix,
  subtractMatrix,
  transpose,
} from "@/lib/tool-kit/linear-algebra/matrix";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  formatStyled,
  type NumberStyle,
  parseRational,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const MATRIX_CALCULATOR_STRINGS = {
  operationLabel: "Operation",
  operations: {
    inverse: "Inverse (A⁻¹)",
    determinant: "Determinant (det A)",
    rref: "Reduced row echelon form (RREF)",
    rank: "Rank",
    transpose: "Transpose (Aᵀ)",
    add: "Add (A + B)",
    subtract: "Subtract (A − B)",
    multiply: "Multiply (A × B)",
    scalar: "Scalar multiple (k × A)",
    power: "Power (Aⁿ)",
    trace: "Trace",
  },
  matrixA: "Matrix A",
  matrixB: "Matrix B",
  rowsOf: "Rows of {matrix}",
  colsOf: "Columns of {matrix}",
  cellLabel: "{matrix}, row {row}, column {col}",
  scalarLabel: "Scalar k",
  powerLabel: "Exponent n (whole number, 0 to 20)",
  entryHint: "Whole numbers, decimals or fractions such as 3/4.",
  results: "Results",
  workingHeading: "The working",
  dimensions: "{rows} × {cols} matrix",
  copyMatrix: "Copy matrix",
  copied: "Copied!",
  enterAll: "Fill every cell of the matrices to see the result.",
  invalid:
    "Enter each entry as a number or a fraction, such as 2, −0.5 or 3/4.",
  needSquare:
    "This operation needs a square matrix, but A is {rows} × {cols}. Make the rows and columns equal.",
  badPower: "Enter the exponent as a whole number from 0 to 20.",
  badScalar: "Enter k as a number or a fraction, such as 2 or −1/3.",
  titles: {
    add: "A + B",
    subtract: "A − B",
    multiply: "A × B",
    scalar: "k × A",
    transpose: "Aᵀ",
    power: "A^{n}",
  },
  entryWorking: "Entry-by-entry working",
  entryLine: "Row {i}, column {j}: {expression} = {result}",
  addNote:
    "Matrices of the same size are added entry by entry: each entry of A + B is the sum of the entries in the same position.",
  subtractNote:
    "Matrices of the same size are subtracted entry by entry: each entry of A − B is the entry of A minus the entry of B in the same position.",
  multiplyNote:
    "Entry (i, j) of A × B is row i of A dotted with column j of B: multiply matching entries and add. B has {rowsB} rows, matching the {colsA} columns of A.",
  scalarNote: "Every entry of A is multiplied by k = {k}.",
  transposeNote:
    "The transpose swaps rows and columns: row i of A becomes column i of Aᵀ.",
  powerNote:
    "Aⁿ is A multiplied by itself n times, here n = {n}; A⁰ is the identity matrix.",
  determinantLabel: "Determinant",
  invertibleLabel: "Invertible?",
  yes: "Yes",
  no: "No",
  det1: "For a 1 × 1 matrix the determinant is its only entry: det = {a}.",
  det2: "det = ad − bc = {a} × {d} − {b} × {c} = {result}",
  cofactorIntro:
    "Cofactor expansion along the first row: each entry is multiplied by its sign and by the determinant of the 2 × 2 minor left when its row and column are deleted.",
  colEntry: "Entry",
  colSign: "Sign",
  colMinor: "Minor",
  colMinorDet: "Minor's determinant",
  colTerm: "Term",
  minorLabel: "Minor for column {col}",
  cofactorSum: "det = {sum} = {result}",
  eliminationIntro:
    "Row-reduce to an upper-triangular matrix. Adding a multiple of one row to another leaves the determinant unchanged; each row swap flips its sign.",
  eliminationProduct:
    "det = ({sign}) × product of the diagonal = ({sign}) × {diagonal} = {result}   [{swaps} row swaps]",
  detZeroNote:
    "A determinant of 0 means the rows are linearly dependent: the matrix is singular and has no inverse.",
  detNonzeroNote:
    "A non-zero determinant means the matrix is invertible and its rows are linearly independent.",
  inverseTitle: "Inverse A⁻¹",
  inverseIntro:
    "Write A next to the identity matrix and row-reduce the left half to I. The same operations turn the right half into A⁻¹.",
  inverseCheck: "Check: A × A⁻¹ equals the identity matrix.",
  singularNote:
    "A is singular: the left half cannot be reduced to the identity (no pivot in column {cols}), so A has no inverse. Its determinant is 0.",
  rrefTitle: "Reduced row echelon form",
  rankLabel: "Rank",
  nullityLabel: "Nullity",
  pivotColumnsLabel: "Pivot columns",
  none: "None",
  rankNote:
    "The rank is the number of pivots (leading 1s). The nullity is the number of columns minus the rank.",
  nullSpaceHeading: "Basis of the null space (solutions of Ax = 0)",
  nullVectorLabel: "A null-space vector",
  stepsSummary:
    "one: Row operations ({count} step)|other: Row operations ({count} steps)",
  noSteps:
    "The matrix is already in the required form, so no row operations are needed.",
  startMatrix: "Starting matrix",
  stepNumber: "Step {n}:",
  afterStep: "Matrix after step {n}",
  opSwap: "R{a} ↔ R{b}",
  opScale: "R{row} → {factor} × R{row}",
  opAdd: "R{row} → R{row} + {factor} × R{source}",
  opSubtract: "R{row} → R{row} − {factor} × R{source}",
  opAddOne: "R{row} → R{row} + R{source}",
  opSubtractOne: "R{row} → R{row} − R{source}",
  traceLabel: "Trace",
  traceWorking: "tr(A) = {parts} = {result}",
} as const;

export type MatrixStrings = typeof MATRIX_CALCULATOR_STRINGS;

type OpId = keyof MatrixStrings["operations"];

const MAX_SIZE = 6;
const SIZES = [1, 2, 3, 4, 5, 6] as const;
const DECIMAL_INPUT = /[.,]/;
const MAX_POWER = 20;

const OP_ORDER: OpId[] = [
  "inverse",
  "determinant",
  "rref",
  "rank",
  "transpose",
  "add",
  "subtract",
  "multiply",
  "scalar",
  "power",
  "trace",
];
const NEEDS_B: ReadonlySet<OpId> = new Set(["add", "subtract", "multiply"]);
const NEEDS_SQUARE: ReadonlySet<OpId> = new Set([
  "inverse",
  "determinant",
  "power",
  "trace",
]);

function padded(rows: string[][]): string[][] {
  return Array.from({ length: MAX_SIZE }, (_, i) =>
    Array.from({ length: MAX_SIZE }, (_, j) => rows[i]?.[j] ?? "0")
  );
}

const DEFAULT_A = padded([
  ["2", "1", "1"],
  ["1", "3", "2"],
  ["1", "0", "0"],
]);
const DEFAULT_B = padded([
  ["1", "0", "2"],
  ["0", "1", "1"],
  ["3", "0", "1"],
]);

function activeCells(
  cells: string[][],
  rows: number,
  cols: number
): string[][] {
  return cells.slice(0, rows).map((row) => row.slice(0, cols));
}

interface Inputs {
  op: OpId;
  a: string[][];
  b: string[][];
  k: string;
  power: string;
}

type Parsed =
  | { ok: true; a: Matrix; b: Matrix; k: ReturnType<typeof parseRational> }
  | { ok: false; message: string; error: boolean };

function parseInputs(inputs: Inputs, strings: MatrixStrings): Parsed {
  const a = parseMatrix(inputs.a);
  const b = NEEDS_B.has(inputs.op) ? parseMatrix(inputs.b) : null;
  const failed = [a, b].find((m) => m !== null && !m.ok);
  if (failed && !failed.ok) {
    return {
      ok: false,
      error: failed.reason === "invalid",
      message: failed.reason === "invalid" ? strings.invalid : strings.enterAll,
    };
  }
  const k = inputs.op === "scalar" ? parseRational(inputs.k) : null;
  if (inputs.op === "scalar" && k === null) {
    return { ok: false, error: true, message: strings.badScalar };
  }
  return {
    ok: true,
    a: a?.ok ? a.matrix : [],
    b: b?.ok ? b.matrix : [],
    k,
  };
}

function Outcome({ inputs, ctx }: { inputs: Inputs; ctx: MatrixCtx }) {
  const { strings } = ctx;
  const parsed = parseInputs(inputs, strings);
  if (!parsed.ok) {
    return parsed.error ? (
      <ErrorNotice>{parsed.message}</ErrorNotice>
    ) : (
      <Notice>{parsed.message}</Notice>
    );
  }
  const { a, b, k } = parsed;
  const { op } = inputs;
  if (NEEDS_SQUARE.has(op) && a.length !== a[0].length) {
    return (
      <Notice>
        {t(strings.needSquare, {
          rows: ctx.fmt(a.length),
          cols: ctx.fmt(a[0].length),
        })}
      </Notice>
    );
  }
  switch (op) {
    case "determinant":
      return <DeterminantResult a={a} ctx={ctx} />;
    case "inverse":
      return <InverseResult a={a} ctx={ctx} />;
    case "rref":
      return <RrefResult a={a} ctx={ctx} showRank={false} />;
    case "rank":
      return <RrefResult a={a} ctx={ctx} showRank />;
    case "trace":
      return <TraceResult a={a} ctx={ctx} />;
    case "power": {
      const n = parseIntegerInput(inputs.power);
      if (n === null || n < 0 || n > MAX_POWER) {
        return <ErrorNotice>{strings.badPower}</ErrorNotice>;
      }
      return (
        <>
          <AnswerMatrix
            ctx={ctx}
            matrix={matrixPower(a, n)}
            title={t(strings.titles.power, { n: ctx.fmt(n) })}
          />
          <p className="text-muted-foreground text-sm">
            {t(strings.powerNote, { n: ctx.fmt(n) })}
          </p>
        </>
      );
    }
    case "transpose":
      return (
        <>
          <AnswerMatrix
            ctx={ctx}
            matrix={transpose(a)}
            title={strings.titles.transpose}
          />
          <p className="text-muted-foreground text-sm">
            {strings.transposeNote}
          </p>
        </>
      );
    default:
      return (
        <ElementWise
          a={a}
          b={b}
          ctx={ctx}
          k={k ? formatStyled(k, ctx.style, ctx.mark) : ""}
          kind={op as "add" | "subtract" | "multiply" | "scalar"}
          scalar={k}
        />
      );
  }
}

function ElementWise({
  kind,
  a,
  b,
  k,
  scalar,
  ctx,
}: {
  kind: "add" | "subtract" | "multiply" | "scalar";
  a: Matrix;
  b: Matrix;
  k: string;
  scalar: ReturnType<typeof parseRational>;
  ctx: MatrixCtx;
}) {
  const { strings } = ctx;
  let result: Matrix = [];
  let note = "";
  if (kind === "add") {
    result = addMatrix(a, b);
    note = strings.addNote;
  } else if (kind === "subtract") {
    result = subtractMatrix(a, b);
    note = strings.subtractNote;
  } else if (kind === "multiply") {
    result = multiplyMatrix(a, b);
    note = t(strings.multiplyNote, {
      rowsB: ctx.fmt(b.length),
      colsA: ctx.fmt(a[0].length),
    });
  } else if (scalar) {
    result = scaleMatrix(a, scalar);
    note = t(strings.scalarNote, { k });
  }
  return (
    <>
      <AnswerMatrix ctx={ctx} matrix={result} title={strings.titles[kind]} />
      <p className="text-muted-foreground text-sm">{note}</p>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <EntryWorking ctx={ctx} lines={entryLines(kind, a, b, result, k, ctx)} />
    </>
  );
}

function sizeOptions(fmt: (n: number) => string) {
  return SIZES.map((n) => ({ value: String(n), label: fmt(n) }));
}

export default function MatrixCalculatorWidget() {
  const strings = useToolUi(MATRIX_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [op, setOp] = useState<OpId>("inverse");
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [bCols, setBCols] = useState(3);
  const [a, setA] = useState(DEFAULT_A);
  const [b, setB] = useState(DEFAULT_B);
  const [k, setK] = useState("2");
  const [power, setPower] = useState("3");

  const needsB = NEEDS_B.has(op);
  const bRows = op === "multiply" ? cols : rows;
  const bWidth = op === "multiply" ? bCols : cols;
  const activeA = activeCells(a, rows, cols);
  const activeB = activeCells(b, bRows, bWidth);
  const allText = [...activeA.flat(), ...(needsB ? activeB.flat() : []), k];
  const style: NumberStyle = allText.some((text) => DECIMAL_INPUT.test(text))
    ? "decimal"
    : "fraction";
  const ctx: MatrixCtx = {
    strings,
    style,
    mark: decimalMarkOf(fmt),
    locale,
    fmt,
  };
  const edit = (set: typeof setA) => (row: number, col: number, text: string) =>
    set((prev) =>
      prev.map((r, i) =>
        i === row ? r.map((c, j) => (j === col ? text : c)) : r
      )
    );
  const cellLabel = (matrix: string) => (row: number, col: number) =>
    t(strings.cellLabel, { matrix, row: fmt(row), col: fmt(col) });
  const options = sizeOptions(fmt);

  return (
    <>
      <SelectField
        id="mc-op"
        label={strings.operationLabel}
        onChange={setOp}
        options={OP_ORDER.map((id) => ({
          value: id,
          label: strings.operations[id],
        }))}
        value={op}
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="px-1 font-medium text-foreground text-sm">
            {strings.matrixA}
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              id="mc-rows"
              label={t(strings.rowsOf, { matrix: "A" })}
              onChange={(v) => setRows(Number(v))}
              options={options}
              value={String(rows)}
            />
            <SelectField
              id="mc-cols"
              label={t(strings.colsOf, { matrix: "A" })}
              onChange={(v) => setCols(Number(v))}
              options={options}
              value={String(cols)}
            />
          </div>
          <MatrixGrid
            cellLabel={cellLabel(strings.matrixA)}
            cells={a}
            cols={cols}
            idPrefix="mc-a"
            onChange={edit(setA)}
            rows={rows}
          />
        </fieldset>
        {needsB ? (
          <fieldset className="space-y-3 rounded-lg border border-border p-4">
            <legend className="px-1 font-medium text-foreground text-sm">
              {strings.matrixB}
            </legend>
            {op === "multiply" ? (
              <SelectField
                id="mc-bcols"
                label={t(strings.colsOf, { matrix: "B" })}
                onChange={(v) => setBCols(Number(v))}
                options={options}
                value={String(bCols)}
              />
            ) : null}
            <MatrixGrid
              cellLabel={cellLabel(strings.matrixB)}
              cells={b}
              cols={bWidth}
              idPrefix="mc-b"
              onChange={edit(setB)}
              rows={bRows}
            />
          </fieldset>
        ) : null}
        {op === "scalar" ? (
          <TextField
            id="mc-k"
            label={strings.scalarLabel}
            mono
            onChange={setK}
            value={k}
          />
        ) : null}
        {op === "power" ? (
          <TextField
            id="mc-n"
            label={strings.powerLabel}
            mono
            onChange={setPower}
            value={power}
          />
        ) : null}
      </div>
      <p className="text-muted-foreground text-xs">{strings.entryHint}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome ctx={ctx} inputs={{ op, a: activeA, b: activeB, k, power }} />
      </section>
    </>
  );
}
